import React, { useState, useEffect, useRef } from 'react';
import { ET_TIMEZONE } from './utils/marketUtils';
import { AI_PROVIDER } from './constants/ai';

// Render star rating
const renderStars = (rating) => {
  return '⭐'.repeat(Math.min(rating, 3));
};

// Extract price from text (finds first dollar amount)
const extractPrice = (text) => {
  if (!text) return null;
  // Match patterns like $135, $132.50, ~$140, $132-133 (takes first number)
  const match = text.match(/\$?([\d,]+\.?\d*)/);
  if (match) {
    return parseFloat(match[1].replace(',', ''));
  }
  return null;
};

// Calculate target prices based on entry price
const calculateTargets = (entryPrice) => {
  if (!entryPrice || isNaN(entryPrice)) return null;
  return {
    t1: { price: entryPrice * 1.01, percent: 1 },
    t2: { price: entryPrice * 1.015, percent: 1.5 },
    t3: { price: entryPrice * 1.02, percent: 2 }
  };
};

// Parse action string into structured parts
const parseAction = (action) => {
  if (!action) return null;

  const parts = {
    entry: null,
    stopLoss: null,
    takeProfit: null,
    takeProfitPrice: null,
    entryPrice: null,
    targets: null,
    raw: action
  };

  // Try to parse structured format: "Entry: ...; Stop loss: ..."
  const entryMatch = action.match(/Entry:?\s*([^;]+)/i);
  const stopMatch = action.match(/Stop\s*loss:?\s*([^;]+)/i);

  if (entryMatch) {
    parts.entry = entryMatch[1].trim();
    parts.entryPrice = extractPrice(parts.entry);
    if (parts.entryPrice) {
      parts.targets = calculateTargets(parts.entryPrice);
    }
  }
  if (stopMatch) parts.stopLoss = stopMatch[1].trim();

  const takeProfitMatch = action.match(/Take\s*profit:?\s*([^;]+)/i);
  if (takeProfitMatch) {
    parts.takeProfit = takeProfitMatch[1].trim();
    parts.takeProfitPrice = extractPrice(parts.takeProfit);
  }

  // Return structured if at least entry was found
  return (parts.entry || parts.stopLoss) ? parts : null;
};

// Action Display Component
const ActionDisplay = ({ action }) => {
  const parsed = parseAction(action);

  // If parsing failed, show raw action
  if (!parsed) {
    return (
      <div style={{ color: '#2563eb', marginTop: '4px' }}>
        <strong>Action:</strong> {action}
      </div>
    );
  }

  return (
    <div style={{
      marginTop: '6px',
      padding: '8px 10px',
      backgroundColor: 'rgba(37, 99, 235, 0.05)',
      borderRadius: '4px',
      borderLeft: '3px solid #2563eb'
    }}>
      <div style={{
        fontWeight: '600',
        color: '#1e40af',
        marginBottom: '4px',
        fontSize: '0.9em'
      }}>
        Action Plan
      </div>
      {parsed.entry && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🟢</span>
          <span><strong style={{ color: '#059669' }}>Entry:</strong> {parsed.entry}</span>
        </div>
      )}
      {parsed.stopLoss && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>🛑</span>
          <span><strong style={{ color: '#dc2626' }}>Stop:</strong> {parsed.stopLoss}</span>
        </div>
      )}
      {parsed.takeProfitPrice && parsed.entryPrice && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '3px' }}>
          <span style={{ flexShrink: 0 }}>📍</span>
          <span>
            <strong style={{ color: '#7c3aed' }}>Take Profit:</strong> ${parsed.takeProfitPrice.toFixed(2)}
            <span style={{ color: '#6b7280', fontSize: '0.9em' }}>
              {' '}(+{((parsed.takeProfitPrice - parsed.entryPrice) / parsed.entryPrice * 100).toFixed(1)}%)
            </span>
          </span>
        </div>
      )}
      {parsed.targets && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
          <span style={{ flexShrink: 0 }}>🎯</span>
          <div>
            <strong style={{ color: '#7c3aed' }}>Target:</strong>
            <div style={{
              marginTop: '2px',
              marginLeft: '4px',
              fontSize: '0.95em',
              lineHeight: '1.6'
            }}>
              <div style={{ color: '#059669' }}>
                T1: ${parsed.targets.t1.price.toFixed(2)} <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t1.percent}%)</span>
              </div>
              <div style={{ color: '#2563eb' }}>
                T2: ${parsed.targets.t2.price.toFixed(2)} <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t2.percent}%)</span>
              </div>
              <div style={{ color: '#7c3aed' }}>
                T3: ${parsed.targets.t3.price.toFixed(2)} <span style={{ color: '#6b7280', fontSize: '0.9em' }}>(+{parsed.targets.t3.percent}%)</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Risk badge color helpers
const getRiskBadgeStyle = (riskLevel) => {
  switch(riskLevel) {
    case 'LOW': return { bg: '#dcfce7', color: '#166534', border: '#22c55e' };
    case 'MEDIUM': return { bg: '#fef3c7', color: '#92400e', border: '#eab308' };
    case 'HIGH': return { bg: '#fee2e2', color: '#991b1b', border: '#ef4444' };
    default: return { bg: '#f3f4f6', color: '#374151', border: '#6b7280' };
  }
};

const getRecBadgeStyle = (rec) => {
  switch(rec) {
    case 'TAKE': return { bg: '#dcfce7', color: '#166534' };
    case 'REDUCE SIZE': return { bg: '#fef3c7', color: '#92400e' };
    case 'SKIP': return { bg: '#fee2e2', color: '#991b1b' };
    default: return { bg: '#f3f4f6', color: '#374151' };
  }
};

// Detailed Pick Card Component (Phase 2: Learning Loop - Personalization)
const DetailedPickCard = ({ pick, index, bgColor, borderColor, stockRisk }) => (
  <div style={{
    backgroundColor: bgColor,
    padding: '10px 12px',
    borderRadius: '6px',
    marginBottom: '8px',
    borderLeft: `3px solid ${borderColor}`
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '6px',
      flexWrap: 'wrap',
      gap: '6px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'bold', fontSize: '0.95em' }}>
          {index + 1}. {pick.symbol} {renderStars(pick.rating)}
        </span>

        {/* Phase 2: Personalization badges - Caution takes priority */}
        {pick.matchesUserSuccessPattern && !pick.matchesUserAvoidPattern && (
          <span style={{
            display: 'inline-block',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '0.75em',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            color: '#92400e',
            border: '1px solid #f59e0b'
          }} title={`Matches your proven pattern: ${pick.userSuccessPatternName || 'successful pattern'}`}>
            ✨ Proven
          </span>
        )}

        {pick.matchesUserAvoidPattern && (
          <span style={{
            display: 'inline-block',
            padding: '2px 6px',
            borderRadius: '3px',
            fontSize: '0.75em',
            fontWeight: '600',
            background: '#fee2e2',
            color: '#991b1b',
            border: '1px solid #dc2626'
          }} title={`Similar to losing pattern: ${pick.userAvoidPatternName || 'avoid pattern'}`}>
            ⚠️ Caution
          </span>
        )}

        {/* Risk badge inline */}
        {stockRisk && (() => {
          const rs = getRiskBadgeStyle(stockRisk.riskLevel);
          const rc = getRecBadgeStyle(stockRisk.recommendation);
          return (
            <>
              <span style={{
                display: 'inline-block',
                padding: '2px 5px',
                borderRadius: '3px',
                fontSize: '0.7em',
                fontWeight: '600',
                backgroundColor: rs.border,
                color: 'white'
              }}>
                🛡️ {stockRisk.riskLevel}
              </span>
              <span style={{
                display: 'inline-block',
                padding: '2px 5px',
                borderRadius: '3px',
                fontSize: '0.7em',
                fontWeight: '600',
                backgroundColor: rc.bg,
                color: rc.color
              }}>
                {stockRisk.recommendation}
              </span>
            </>
          );
        })()}
      </div>

      <span style={{
        color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
        fontWeight: '600',
        fontSize: '0.95em'
      }}>
        {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain?.toFixed(2)}%
      </span>
    </div>

    <div style={{ fontSize: '0.85em', color: '#4b5563', lineHeight: '1.5' }}>
      <div><strong>Trend:</strong> {pick.trend}</div>
      {pick.pattern && <div><strong>Pattern:</strong> {pick.pattern}</div>}
      <ActionDisplay action={pick.action} />
    </div>

    {/* Phase 2: Personalization note - layout matches risk detail block */}
    {pick.personalizationNote && (() => {
      const isCaution = pick.matchesUserAvoidPattern;
      return (
        <div style={{
          background: isCaution ? '#fef2f2' : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderLeft: isCaution ? '3px solid #dc2626' : '3px solid #f59e0b',
          padding: '8px 10px',
          margin: '8px 0 0 0',
          borderRadius: '4px',
          fontSize: '0.8em',
          color: isCaution ? '#991b1b' : '#78350f',
          fontWeight: '500',
          lineHeight: '1.4'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
            {isCaution ? (
              <span style={{
                padding: '1px 5px',
                borderRadius: '3px',
                fontSize: '0.85em',
                fontWeight: '600',
                background: '#fee2e2',
                color: '#991b1b',
                border: '1px solid #dc2626'
              }}>⚠️ Caution</span>
            ) : (
              <span style={{
                padding: '1px 5px',
                borderRadius: '3px',
                fontSize: '0.85em',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                color: '#92400e',
                border: '1px solid #f59e0b'
              }}>✨ Proven</span>
            )}
          </div>
          <div style={{ lineHeight: '1.4' }}>
            {pick.personalizationNote.replace(/^[✨⚠️]\s*/, '')}
          </div>
        </div>
      );
    })()}

    {/* Risk Assessment Block */}
    {stockRisk && (() => {
      const rStyle = getRiskBadgeStyle(stockRisk.riskLevel);
      const recStyle = getRecBadgeStyle(stockRisk.recommendation);
      return (
        <div style={{
          marginTop: '8px',
          padding: '8px 10px',
          borderRadius: '4px',
          backgroundColor: rStyle.bg,
          borderLeft: `3px solid ${rStyle.border}`,
          fontSize: '0.85em'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: stockRisk.rationale ? '4px' : '0' }}>
            <span style={{
              padding: '1px 5px',
              borderRadius: '3px',
              fontSize: '0.85em',
              fontWeight: '600',
              backgroundColor: rStyle.border,
              color: 'white'
            }}>
              🛡️ {stockRisk.riskLevel}
            </span>
            <span style={{
              padding: '1px 5px',
              borderRadius: '3px',
              fontSize: '0.85em',
              fontWeight: '600',
              backgroundColor: recStyle.bg,
              color: recStyle.color
            }}>
              {stockRisk.recommendation}
            </span>
            {stockRisk.bothAIsAgree && (
              <span style={{ fontSize: '0.85em', color: '#166534' }}>Both AIs</span>
            )}
          </div>
          {stockRisk.rationale && (
            <div style={{ color: rStyle.color, lineHeight: '1.4' }}>
              {stockRisk.rationale}
            </div>
          )}
        </div>
      );
    })()}
  </div>
);

// Detailed Top Picks Comparison Component
const DetailedPicksComparison = ({ anthropicResult, openaiResult, riskAssessment }) => {
  if (!anthropicResult || !openaiResult || anthropicResult.error || openaiResult.error) {
    return null;
  }

  const claudePicksData = anthropicResult.topPicks || [];
  const gptPicksData = openaiResult.topPicks || [];
  const maxPicks = Math.max(claudePicksData.length, gptPicksData.length);

  if (maxPicks === 0) return null;

  // Build a lookup map from risk assessment: symbol → StockRisk
  const riskMap = {};
  if (riskAssessment?.stockRisks) {
    for (const risk of riskAssessment.stockRisks) {
      riskMap[risk.symbol] = risk;
    }
  }

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '1.2em' }}>🔥</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>Detailed Top Picks Comparison</span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px'
        }}>
          {/* Claude Column */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '10px',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.95em',
              paddingBottom: '8px',
              borderBottom: '2px solid #7c3aed',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              <span>🟣</span> {process.env.REACT_APP_ANTHROPIC_MODEL_NAME || 'Claude'}
            </div>
            <div style={{
              maxHeight: '600px',
              overflowY: 'auto',
              paddingRight: '4px'
            }}>
              {claudePicksData.map((pick, i) => (
                <DetailedPickCard
                  key={pick.symbol}
                  pick={pick}
                  index={i}
                  bgColor="#faf5ff"
                  borderColor={pick.rating >= 3 ? '#22c55e' : pick.rating >= 2 ? '#eab308' : '#6b7280'}
                  stockRisk={riskMap[pick.symbol]}
                />
              ))}
            </div>
          </div>

          {/* GPT Column */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '10px',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.95em',
              paddingBottom: '8px',
              borderBottom: '2px solid #059669',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              <span>🟢</span> {process.env.REACT_APP_OPENAI_MODEL_NAME || 'GPT'}
            </div>
            <div style={{
              maxHeight: '600px',
              overflowY: 'auto',
              paddingRight: '4px'
            }}>
              {gptPicksData.map((pick, i) => (
                <DetailedPickCard
                  key={pick.symbol}
                  pick={pick}
                  index={i}
                  bgColor="#f0fdf4"
                  borderColor={pick.rating >= 3 ? '#22c55e' : pick.rating >= 2 ? '#eab308' : '#6b7280'}
                  stockRisk={riskMap[pick.symbol]}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Comparison Summary Component
const ComparisonSummary = ({ anthropicResult, openaiResult }) => {
  if (!anthropicResult || !openaiResult || anthropicResult.error || openaiResult.error) {
    return null;
  }

  const claudePicksData = anthropicResult.topPicks || [];
  const gptPicksData = openaiResult.topPicks || [];
  const claudePicks = claudePicksData.map(p => p.symbol);
  const gptPicks = gptPicksData.map(p => p.symbol);

  // Find consensus
  const consensusPicks = claudePicks.filter(s => gptPicks.includes(s));

  // Get rating for a symbol from picks data
  const getClaudeRating = (symbol) => claudePicksData.find(p => p.symbol === symbol)?.rating || 0;
  const getGptRating = (symbol) => gptPicksData.find(p => p.symbol === symbol)?.rating || 0;
  const getClaudeDailyGain = (symbol) => claudePicksData.find(p => p.symbol === symbol)?.dailyGain;
  const getGptDailyGain = (symbol) => gptPicksData.find(p => p.symbol === symbol)?.dailyGain;

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '1.2em' }}>🏆</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>Top Picks</span>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* Consensus Picks */}
        {consensusPicks.length > 0 && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '6px',
              color: '#16a34a',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>✅</span>
              <span>Both Recommend ({consensusPicks.length})</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {consensusPicks.map(symbol => {
                const avgRating = Math.round((getClaudeRating(symbol) + getGptRating(symbol)) / 2);
                const dailyGain = getClaudeDailyGain(symbol) ?? getGptDailyGain(symbol);
                return (
                  <span key={symbol} style={{
                    padding: '4px 10px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '4px',
                    fontSize: '0.95em',
                    color: '#166534',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ fontWeight: '500' }}>{symbol} {renderStars(avgRating)}</span>
                    {dailyGain != null && (
                      <span style={{
                        color: dailyGain >= 0 ? '#22c55e' : '#ef4444',
                        fontWeight: '600',
                        fontSize: '0.95em'
                      }}>
                        {dailyGain >= 0 ? '+' : ''}{dailyGain.toFixed(2)}%
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Picks Comparison */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px'
        }}>
          {/* Claude Picks */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '6px',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.9em'
            }}>
              <span>🟣</span> Claude Top {claudePicks.length}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {claudePicksData.map((pick, i) => (
                <span key={pick.symbol} style={{
                  padding: '3px 8px',
                  backgroundColor: consensusPicks.includes(pick.symbol) ? '#dcfce7' : '#f3e8ff',
                  borderRadius: '4px',
                  fontSize: '0.95em',
                  color: consensusPicks.includes(pick.symbol) ? '#166534' : '#6b21a8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontWeight: '500' }}>{i + 1}. {pick.symbol} {renderStars(pick.rating)}</span>
                  {pick.dailyGain != null && (
                    <span style={{
                      color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '0.95em',
                      marginLeft: '8px'
                    }}>
                      {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain.toFixed(2)}%
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* GPT Picks */}
          <div>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '6px',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.9em'
            }}>
              <span>🟢</span> GPT Top {gptPicks.length}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {gptPicksData.map((pick, i) => (
                <span key={pick.symbol} style={{
                  padding: '3px 8px',
                  backgroundColor: consensusPicks.includes(pick.symbol) ? '#dcfce7' : '#d1fae5',
                  borderRadius: '4px',
                  fontSize: '0.95em',
                  color: consensusPicks.includes(pick.symbol) ? '#166534' : '#065f46',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ fontWeight: '500' }}>{i + 1}. {pick.symbol} {renderStars(pick.rating)}</span>
                  {pick.dailyGain != null && (
                    <span style={{
                      color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '0.95em',
                      marginLeft: '8px'
                    }}>
                      {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain.toFixed(2)}%
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Single AI result card
const AiResultCard = ({ result, isLoading, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (isLoading) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderRadius: '8px',
        marginBottom: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ fontSize: '1.2em' }}>⏳</span>
          <span style={{ fontWeight: 'bold', color: '#6b7280' }}>Analyzing...</span>
        </div>
        <div style={{ color: '#9ca3af' }}>Please wait while AI analyzes the stock data</div>
      </div>
    );
  }

  if (!result) return null;

  if (result.error) {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
        marginBottom: '12px',
        border: '1px solid #fecaca'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '1.2em' }}>❌</span>
          <span style={{ fontWeight: 'bold', color: '#dc2626' }}>
            {result.provider === AI_PROVIDER.ANTHROPIC ? 'Claude' : 'GPT'} Error
          </span>
        </div>
        <div style={{ color: '#dc2626', fontSize: '0.9em' }}>{result.error}</div>
      </div>
    );
  }

  const providerIcon = result.provider === AI_PROVIDER.ANTHROPIC ? '🟣' : '🟢';
  const providerName = result.provider === AI_PROVIDER.ANTHROPIC ? (process.env.REACT_APP_ANTHROPIC_MODEL_NAME || 'Claude') : (process.env.REACT_APP_OPENAI_MODEL_NAME || 'GPT');
  const bgColor = result.provider === AI_PROVIDER.ANTHROPIC ? '#faf5ff' : '#f0fdf4';

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Collapsible Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '12px 16px',
          backgroundColor: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.2em' }}>{providerIcon}</span>
          <span style={{ fontWeight: 'bold' }}>{providerName}</span>
          {result.inputTokens && (
            <span style={{ fontSize: '0.75em', color: '#9ca3af' }}>
              {result.inputTokens + result.outputTokens} tokens
            </span>
          )}
        </div>
        <span style={{ color: '#6b7280', fontSize: '1.2em' }}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div style={{ padding: '16px' }}>
          {/* Summary */}
          {result.summary && (
            <div style={{
              backgroundColor: '#f0fdf4',
              padding: '10px 14px',
              borderRadius: '6px',
              marginBottom: '12px',
              fontSize: '1em',
              color: '#166534',
              lineHeight: '1.5'
            }}>
              {result.summary}
            </div>
          )}

          {/* Top Picks */}
          {result.topPicks && result.topPicks.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>🔥</span>
                <span>Top {result.topPicks.length} Picks</span>
              </div>
              {result.topPicks.map((pick, index) => (
                <div key={pick.symbol} style={{
                  backgroundColor: '#f9fafb',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  borderLeft: `3px solid ${pick.rating >= 3 ? '#22c55e' : pick.rating >= 2 ? '#eab308' : '#6b7280'}`
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '4px',
                    flexWrap: 'wrap',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 'bold' }}>
                        {index + 1}. {pick.symbol} {renderStars(pick.rating)}
                      </span>

                      {/* Phase 2: Personalization badges - Caution takes priority */}
                      {pick.matchesUserSuccessPattern && !pick.matchesUserAvoidPattern && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '0.75em',
                          fontWeight: '600',
                          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          color: '#92400e',
                          border: '1px solid #f59e0b'
                        }} title={`Matches: ${pick.userSuccessPatternName || 'proven pattern'}`}>
                          ✨ Proven
                        </span>
                      )}

                      {pick.matchesUserAvoidPattern && (
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '0.75em',
                          fontWeight: '600',
                          background: '#fee2e2',
                          color: '#991b1b',
                          border: '1px solid #dc2626'
                        }} title={`Similar to: ${pick.userAvoidPatternName || 'avoid pattern'}`}>
                          ⚠️ Caution
                        </span>
                      )}
                    </div>

                    <span style={{
                      color: pick.dailyGain >= 0 ? '#22c55e' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '0.95em'
                    }}>
                      {pick.dailyGain >= 0 ? '+' : ''}{pick.dailyGain?.toFixed(2)}%
                    </span>
                  </div>
                  <div style={{ fontSize: '0.95em', color: '#4b5563', lineHeight: '1.6' }}>
                    <div><strong>Trend:</strong> {pick.trend}</div>
                    {pick.pattern && <div><strong>Pattern:</strong> {pick.pattern}</div>}
                    <ActionDisplay action={pick.action} />
                  </div>

                  {/* Phase 2: Personalization note - layout matches risk detail block */}
                  {pick.personalizationNote && (() => {
                    const isCaution = pick.matchesUserAvoidPattern;
                    return (
                      <div style={{
                        background: isCaution ? '#fef2f2' : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                        borderLeft: isCaution ? '3px solid #dc2626' : '3px solid #f59e0b',
                        padding: '8px 10px',
                        margin: '8px 0 0 0',
                        borderRadius: '4px',
                        fontSize: '0.85em',
                        color: isCaution ? '#991b1b' : '#78350f',
                        fontWeight: '500',
                        lineHeight: '1.4'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                          {isCaution ? (
                            <span style={{
                              padding: '1px 5px',
                              borderRadius: '3px',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              background: '#fee2e2',
                              color: '#991b1b',
                              border: '1px solid #dc2626'
                            }}>⚠️ Caution</span>
                          ) : (
                            <span style={{
                              padding: '1px 5px',
                              borderRadius: '3px',
                              fontSize: '0.85em',
                              fontWeight: '600',
                              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                              color: '#92400e',
                              border: '1px solid #f59e0b'
                            }}>✨ Proven</span>
                          )}
                        </div>
                        <div style={{ lineHeight: '1.4' }}>
                          {pick.personalizationNote.replace(/^[✨⚠️]\s*/, '')}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}

          {/* Avoid */}
          {result.avoid && result.avoid.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#dc2626'
              }}>
                <span>⚠️</span>
                <span>Avoid</span>
              </div>
              {result.avoid.map((stock) => (
                <div key={stock.symbol} style={{
                  backgroundColor: '#fef2f2',
                  padding: '10px 14px',
                  borderRadius: '6px',
                  marginBottom: '6px',
                  fontSize: '1em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontWeight: 'bold', color: '#dc2626' }}>{stock.symbol}</span>
                  <span style={{ color: '#7f1d1d' }}>- {stock.reason}</span>
                </div>
              ))}
            </div>
          )}

          {/* Detailed Analysis */}
          {result.analysis && (
            <div style={{ marginTop: '12px' }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '8px',
                fontSize: '1em',
                color: '#6b7280'
              }}>
                Analysis
              </div>
              <div style={{
                fontSize: '0.95em',
                color: '#4b5563',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {result.analysis}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Risk Assessment Section Component
const RiskAssessmentSection = ({ riskAssessment, isLoading }) => {
  if (isLoading) {
    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        marginBottom: '12px',
        border: '1px solid #e5e7eb',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fef3c7',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '1.2em' }}>🛡️</span>
          <span style={{ fontWeight: 'bold', color: '#92400e' }}>Risk Assessment</span>
          <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>Analyzing risk...</span>
        </div>
        <div style={{ textAlign: 'center', padding: '30px 20px', color: '#6b7280' }}>
          <div style={{ fontSize: '1.5em', marginBottom: '8px' }}>🔍</div>
          <div style={{ fontSize: '0.9em' }}>Reviewing scan recommendations for risks...</div>
        </div>
      </div>
    );
  }

  if (!riskAssessment || riskAssessment.error) return null;

  const getRiskColor = (level) => {
    switch(level) {
      case 'LOW': return { bg: '#f0fdf4', border: '#22c55e', text: '#166534', badge: '#22c55e' };
      case 'MEDIUM': return { bg: '#fefce8', border: '#eab308', text: '#854d0e', badge: '#eab308' };
      case 'HIGH': return { bg: '#fef2f2', border: '#ef4444', text: '#991b1b', badge: '#ef4444' };
      default: return { bg: '#f9fafb', border: '#6b7280', text: '#374151', badge: '#6b7280' };
    }
  };

  const getRecStyle = (rec) => {
    switch(rec) {
      case 'TAKE': return { bg: '#dcfce7', color: '#166534' };
      case 'REDUCE SIZE': return { bg: '#fef3c7', color: '#92400e' };
      case 'SKIP': return { bg: '#fee2e2', color: '#991b1b' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      marginBottom: '12px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#fef3c7',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '1.2em' }}>🛡️</span>
        <span style={{ fontWeight: 'bold', color: '#92400e' }}>Risk Assessment</span>
        {riskAssessment.inputTokens && (
          <span style={{ fontSize: '0.75em', color: '#9ca3af', marginLeft: 'auto' }}>
            {riskAssessment.inputTokens + riskAssessment.outputTokens} tokens
          </span>
        )}
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* Overall Risk Summary */}
        {riskAssessment.overallRiskSummary && (
          <div style={{
            backgroundColor: '#fffbeb',
            padding: '10px 14px',
            borderRadius: '6px',
            marginBottom: '12px',
            borderLeft: '3px solid #f59e0b',
            fontSize: '0.95em',
            color: '#78350f',
            lineHeight: '1.5'
          }}>
            {riskAssessment.overallRiskSummary}
          </div>
        )}

        {/* Per-Stock Risk Cards */}
        {riskAssessment.stockRisks?.map((risk) => {
          const riskColors = getRiskColor(risk.riskLevel);
          const recStyle = getRecStyle(risk.recommendation);
          return (
            <div key={risk.symbol} style={{
              backgroundColor: riskColors.bg,
              padding: '10px 12px',
              borderRadius: '6px',
              marginBottom: '8px',
              borderLeft: `3px solid ${riskColors.border}`
            }}>
              {/* Symbol + Risk Badge + Recommendation Badge */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '6px',
                flexWrap: 'wrap',
                gap: '4px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '1em' }}>{risk.symbol}</span>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '0.75em',
                    fontWeight: '600',
                    backgroundColor: riskColors.badge,
                    color: 'white'
                  }}>
                    {risk.riskLevel} ({risk.riskScore}/10)
                  </span>
                  {risk.bothAIsAgree && (
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '0.75em',
                      backgroundColor: '#dcfce7',
                      color: '#166534'
                    }}>
                      Both AIs agree
                    </span>
                  )}
                </div>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '3px',
                  fontSize: '0.8em',
                  fontWeight: '600',
                  backgroundColor: recStyle.bg,
                  color: recStyle.color
                }}>
                  {risk.recommendation}
                </span>
              </div>

              {/* Risk Factors */}
              {risk.riskFactors?.length > 0 && (
                <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
                  {risk.riskFactors.map((factor, i) => (
                    <div key={i} style={{ marginBottom: '2px' }}>- {factor}</div>
                  ))}
                </div>
              )}

              {/* Rationale */}
              {risk.rationale && (
                <div style={{
                  marginTop: '6px',
                  fontSize: '0.85em',
                  color: riskColors.text,
                  fontStyle: 'italic'
                }}>
                  {risk.rationale}
                </div>
              )}
            </div>
          );
        })}

        {/* Conflict Alerts */}
        {riskAssessment.conflicts?.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#dc2626',
              fontSize: '0.95em'
            }}>
              Conflicting Recommendations
            </div>
            {riskAssessment.conflicts.map((conflict, i) => (
              <div key={i} style={{
                backgroundColor: '#fef2f2',
                padding: '8px 12px',
                borderRadius: '6px',
                marginBottom: '6px',
                borderLeft: '3px solid #dc2626',
                fontSize: '0.85em'
              }}>
                <div style={{ fontWeight: '600', color: '#991b1b', marginBottom: '4px' }}>
                  {conflict.symbol}
                </div>
                <div style={{ color: '#7c3aed' }}>Claude: {conflict.claudeView}</div>
                <div style={{ color: '#059669' }}>GPT: {conflict.gptView}</div>
                {conflict.riskImplication && (
                  <div style={{ color: '#6b7280', marginTop: '4px' }}>
                    {conflict.riskImplication}
                  </div>
                )}
                <div style={{
                  marginTop: '4px',
                  color: '#374151',
                  fontStyle: 'italic'
                }}>
                  {conflict.resolution}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Missed Risks */}
        {riskAssessment.missedRisks?.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              color: '#b45309',
              fontSize: '0.95em'
            }}>
              Watch Out For
            </div>
            {riskAssessment.missedRisks.map((risk, i) => (
              <div key={i} style={{
                backgroundColor: '#fef3c7',
                padding: '6px 10px',
                borderRadius: '4px',
                marginBottom: '4px',
                fontSize: '0.85em',
                color: '#78350f'
              }}>
                {risk}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Format scan time for display (24-hour format with seconds to match market time)
const formatScanTime = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: ET_TIMEZONE
  }) + ' ET';
};

// Main AI Analysis Panel
const AiAnalysisPanel = ({
  isOpen,
  onClose,
  anthropicResult,
  openaiResult,
  isAnthropicLoading = false,
  isOpenaiLoading = false,
  onAnalyze,
  scanTime,
  width = 600,
  stockCountOptions = [100, 120, 150, 200],
  stockCount = 100,
  onStockCountChange,
  riskAssessment = null,
  isRiskLoading = false,
  onSaveScan,
  isScanSaved = false
}) => {
  const [isStockCountMenuOpen, setIsStockCountMenuOpen] = useState(false);
  const [customCount, setCustomCount] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const dropdownRef = useRef(null);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStockCountMenuOpen(false);
      }
    };

    if (isStockCountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStockCountMenuOpen]);

  if (!isOpen) return null;

  const isLoading = isAnthropicLoading || isOpenaiLoading || isRiskLoading;
  const hasResults = anthropicResult || openaiResult;
  const hasBothResults = anthropicResult && openaiResult && !anthropicResult.error && !openaiResult.error;

  const handleStockCountSelect = (count) => {
    if (count === 'custom') {
      setShowCustomInput(true);
      setCustomCount(stockCount.toString());
    } else {
      onStockCountChange(count);
      setShowCustomInput(false);
    }
    setIsStockCountMenuOpen(false);
  };

  const handleCustomCountSubmit = () => {
    const count = parseInt(customCount, 10);
    if (count >= 50 && count <= 500) {
      onStockCountChange(count);
      setShowCustomInput(false);
    }
  };

  return (
    <div style={{
      width: `${width}px`,
      minWidth: '300px',
      borderLeft: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontSize: '0.95em'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.3em' }}>🤖</span>
          <span style={{ fontWeight: '600', fontSize: '1.1em', color: 'white' }}>AI Scan</span>
          {scanTime && (
            <span style={{
              fontSize: '0.9em',
              color: 'rgba(255,255,255,0.9)',
              marginLeft: '8px',
              fontWeight: '500'
            }}>
              {formatScanTime(scanTime)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Stock Count Dropdown */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            {showCustomInput ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="number"
                  value={customCount}
                  onChange={(e) => setCustomCount(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomCountSubmit()}
                  placeholder="50-500"
                  style={{
                    width: '60px',
                    padding: '4px 8px',
                    fontSize: '0.85em',
                    borderRadius: '4px',
                    border: 'none',
                    textAlign: 'center'
                  }}
                  min="50"
                  max="500"
                  autoFocus
                />
                <button
                  onClick={handleCustomCountSubmit}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.8em',
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✓
                </button>
                <button
                  onClick={() => setShowCustomInput(false)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '0.8em',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsStockCountMenuOpen(!isStockCountMenuOpen)}
                disabled={isLoading}
                style={{
                  padding: '4px 10px',
                  fontSize: '0.85em',
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <span>{stockCount}</span>
                <span style={{ fontSize: '0.8em' }}>▼</span>
              </button>
            )}
            {isStockCountMenuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: '100px',
                overflow: 'hidden'
              }}>
                {stockCountOptions.map((count) => (
                  <div
                    key={count}
                    onClick={() => handleStockCountSelect(count)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: stockCount === count ? '#eff6ff' : 'transparent',
                      borderBottom: '1px solid #f3f4f6',
                      fontSize: '0.9em'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = stockCount === count ? '#eff6ff' : '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = stockCount === count ? '#eff6ff' : 'transparent'}
                  >
                    <span style={{ fontWeight: stockCount === count ? 'bold' : 'normal' }}>{count}</span>
                    {stockCount === count && <span style={{ color: '#22c55e' }}>✓</span>}
                  </div>
                ))}
                <div
                  onClick={() => handleStockCountSelect('custom')}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    backgroundColor: 'transparent',
                    fontSize: '0.9em',
                    color: '#6b7280'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span>⚙️</span>
                  <span>Custom</span>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onAnalyze}
            disabled={isLoading}
            style={{
              padding: '6px 12px',
              fontSize: '0.85em',
              backgroundColor: isLoading ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => !isLoading && (e.target.style.backgroundColor = 'rgba(255,255,255,0.3)')}
            onMouseOut={(e) => !isLoading && (e.target.style.backgroundColor = 'rgba(255,255,255,0.2)')}
          >
            {isLoading ? (isAnthropicLoading && isOpenaiLoading ? '⏳ Analyzing...' : '⏳ Waiting...') : '🔄 Refresh'}
          </button>
          {hasBothResults && riskAssessment && !riskAssessment.error && !isLoading && (
            <button
              onClick={onSaveScan}
              disabled={isScanSaved || isLoading}
              title={isScanSaved ? 'Scan already saved' : 'Save scan to database'}
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                backgroundColor: isScanSaved ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.2)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isScanSaved ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => !isScanSaved && (e.target.style.backgroundColor = 'rgba(255,255,255,0.3)')}
              onMouseOut={(e) => !isScanSaved && (e.target.style.backgroundColor = 'rgba(255,255,255,0.2)')}
            >
              {isScanSaved ? '✓ Saved' : '💾 Save Scan'}
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '0',
              width: '32px',
              height: '32px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '20px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px'
      }}>
        {!hasResults && !isLoading ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280'
          }}>
            <div style={{ fontSize: '2em', marginBottom: '12px' }}>🤖</div>
            <div style={{ marginBottom: '16px' }}>
              Click the button above to analyze current page stocks with AI
            </div>
            <button
              onClick={onAnalyze}
              style={{
                padding: '10px 24px',
                fontSize: '1em',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              🚀 Start Analysis
            </button>
          </div>
        ) : (
          <>
            {/* Progressive Loading Status */}
            {isLoading && (
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                {/* Claude Status */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: isAnthropicLoading ? '#faf5ff' : (anthropicResult?.error ? '#fef2f2' : '#f0fdf4')
                }}>
                  <span style={{ fontSize: '1.1em' }}>🟣</span>
                  <span style={{ fontWeight: '500', color: '#7c3aed' }}>Claude</span>
                  {isAnthropicLoading ? (
                    <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>⏳ Loading...</span>
                  ) : anthropicResult?.error ? (
                    <span style={{ color: '#dc2626', fontSize: '0.9em' }}>❌ Error</span>
                  ) : (
                    <span style={{ color: '#22c55e', fontSize: '0.9em' }}>✅ Done</span>
                  )}
                </div>

                {/* OpenAI Status */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: isOpenaiLoading ? '#f0fdf4' : (openaiResult?.error ? '#fef2f2' : '#f0fdf4')
                }}>
                  <span style={{ fontSize: '1.1em' }}>🟢</span>
                  <span style={{ fontWeight: '500', color: '#059669' }}>GPT</span>
                  {isOpenaiLoading ? (
                    <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>⏳ Loading...</span>
                  ) : openaiResult?.error ? (
                    <span style={{ color: '#dc2626', fontSize: '0.9em' }}>❌ Error</span>
                  ) : (
                    <span style={{ color: '#22c55e', fontSize: '0.9em' }}>✅ Done</span>
                  )}
                </div>

                {/* Risk Assessment Status */}
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: isRiskLoading ? '#fef3c7' :
                    (riskAssessment?.error ? '#fef2f2' :
                     riskAssessment ? '#f0fdf4' : '#f9fafb')
                }}>
                  <span style={{ fontSize: '1.1em' }}>🛡️</span>
                  <span style={{ fontWeight: '500', color: '#92400e' }}>Risk</span>
                  {isRiskLoading ? (
                    <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>⏳ Assessing...</span>
                  ) : riskAssessment?.error ? (
                    <span style={{ color: '#dc2626', fontSize: '0.9em' }}>❌ Error</span>
                  ) : riskAssessment ? (
                    <span style={{ color: '#22c55e', fontSize: '0.9em' }}>✅ Done</span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '0.9em' }}>Waiting</span>
                  )}
                </div>
              </div>
            )}

            {/* Waiting message - show when loading and not both results yet */}
            {isLoading && !hasBothResults && (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#6b7280'
              }}>
                <div style={{ fontSize: '2em', marginBottom: '12px' }}>🔍</div>
                <div style={{ fontSize: '1em', color: '#4b5563' }}>
                  Analyzing stocks with AI...
                </div>
                <div style={{ fontSize: '0.9em', color: '#9ca3af', marginTop: '8px' }}>
                  Results will display when both AIs complete
                </div>
              </div>
            )}

            {/* Comparison Summary - Only show when both results are available */}
            {hasBothResults && (
              <ComparisonSummary anthropicResult={anthropicResult} openaiResult={openaiResult} />
            )}

            {/* Detailed Top Picks Comparison */}
            {hasBothResults && (
              <DetailedPicksComparison anthropicResult={anthropicResult} openaiResult={openaiResult} riskAssessment={riskAssessment} />
            )}

            {/* Risk Assessment Section */}
            {(hasBothResults || isRiskLoading) && (
              <RiskAssessmentSection riskAssessment={riskAssessment} isLoading={isRiskLoading} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AiAnalysisPanel;
